import { buildEventParkingServiceUrl, EVENT_PARKING_LAYER_CONFIG, type EventParkingLayerConfig, type EventParkingLayerId } from '../config/eventParkingConfig';

const EVENT_NAMES_TABLE_URL = () => `${buildEventParkingServiceUrl()}/18/query`;

/**
 * Queries the Event Names table and returns a Set of event_name values
 * where active = 'Yes'.
 */
export async function fetchActiveEventNames(): Promise<Set<string>> {
  const params = new URLSearchParams({
    where: "active = 'Yes'",
    outFields: 'event_name',
    returnGeometry: 'false',
    f: 'json',
  });

  const response = await fetch(`${EVENT_NAMES_TABLE_URL()}?${params}`);
  const data = await response.json();

  const activeNames = new Set<string>();
  for (const feature of (data.features ?? []) as { attributes: { event_name: string } }[]) {
    const name = feature.attributes?.event_name;
    if (name) activeNames.add(name);
  }
  return activeNames;
}

/**
 * Given the active event names from the table, returns which layerIds are enabled.
 * A layer is enabled if ANY of its match values appear in activeNames (exact match).
 *
 * Uses `eventTableName` when present (explicit override for Event Names table values
 * that differ from `aliasEventType`); falls back to `aliasEventType`.
 *
 * The `config` parameter is injectable for testing; defaults to the full EVENT_PARKING_LAYER_CONFIG.
 */
export function resolveEnabledLayersFromActiveNames(
  activeNames: Set<string>,
  config: Array<Pick<EventParkingLayerConfig, 'layerId' | 'aliasEventType' | 'eventTableName'>> = EVENT_PARKING_LAYER_CONFIG,
): Partial<Record<EventParkingLayerId, boolean>> {
  return config.reduce(
    (acc, { layerId, aliasEventType, eventTableName }) => {
      const matchSource = eventTableName ?? aliasEventType;
      const types = Array.isArray(matchSource) ? matchSource : [matchSource];
      acc[layerId] = types.some((t) => activeNames.has(t));
      return acc;
    },
    {} as Partial<Record<EventParkingLayerId, boolean>>,
  );
}

export const EVENT_PARKING_QUERY_PARAM_MAP: Record<string, EventParkingLayerId> = {
  wbb: 'wbbParking',
  mbb: 'mbbParking',
  commencement: 'commencementParking',
  convocationhuntsman: 'convocationHuntsmanParking',
  convocationkingsbury: 'convocationKingsburyParking',
  convocationstudenunion: 'convocationStudentUnionParking',
  monster: 'monsterJamParking',
  supercross: 'supercrossParking',
  concert: 'stadiumConcertParking',
  gymnastics: 'uGymnasticsParking',
  hsfootball: 'hsFootballParking',
  hsbball: 'hsBasketballPlayoffParking',
};

export const getEventParkingVisibilityFromQuery = (
  search: string,
): Record<EventParkingLayerId, boolean> => {
  const params = new URLSearchParams(search);

  const visibilityByLayer = EVENT_PARKING_LAYER_CONFIG.reduce<Record<EventParkingLayerId, boolean>>(
    (acc, layerConfig) => {
      acc[layerConfig.layerId] = false;
      return acc;
    },
    {} as Record<EventParkingLayerId, boolean>,
  );

  Object.entries(EVENT_PARKING_QUERY_PARAM_MAP).forEach(([paramName, layerId]) => {
    visibilityByLayer[layerId] = params.get(paramName)?.toLowerCase() === 'on';
  });

  return visibilityByLayer;
};
